// Alerts panel — account-aware, time-aware operations feed.
//
// Two sections:
//   Active  — current snapshot (REST poll + WS), filtered to the desk's
//             account scope. Aggregate alerts (account_id null, e.g.
//             portfolio-level breaches) always show. When peeking a single
//             account, alerts on OTHER accounts surface as a muted strip
//             so scope-switching never hides a critical elsewhere.
//   History — session-accumulated alerts, deduped by (preset, type,
//             account) with an occurrence count, newest first, relative
//             timestamps. Severity is color, not sort order.
//
// Multi-account: SleeveAlert.account_id (added 2026-09) carries the scope
// the alert was computed for; null = aggregate across all accounts.

import { useMemo, useState } from "react";
import type { Account, SleeveAlert, AlertSeverity } from "../api/types";

interface Props {
  /** Live alerts (latest snapshot from WS or poll). Displayed as "active". */
  activeAlerts: SleeveAlert[];
  /** Historical alerts accumulated over the session (newest first). */
  history: SleeveAlert[];
  /** Clear the history log. */
  onClearHistory: () => void;
  /** Account list for badge labels/kinds. */
  accounts: Account[];
  /** Current desk account scope (null = All accounts). */
  scopeAccountId: number | null;
}

type SeverityFilter = "all" | AlertSeverity;

/** Stable dedup key for an alert condition. */
export function alertKey(a: SleeveAlert): string {
  return `${a.preset}|${a.alert_type}|${a.account_id ?? "agg"}`;
}

function relTime(ts?: string): string {
  if (!ts) return "";
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** True when the alert belongs in the current desk scope. Aggregate
 *  alerts (account_id null) always show — they affect every account. */
function inScope(a: SleeveAlert, scopeAccountId: number | null): boolean {
  return scopeAccountId == null || a.account_id == null || a.account_id === scopeAccountId;
}

function fmtMetric(a: SleeveAlert): string {
  const isMoney = /drawdown/i.test(a.alert_type);
  const v = isMoney ? `$${a.metric_value.toFixed(0)}` : a.metric_value.toFixed(2);
  const t = isMoney ? `$${a.threshold.toFixed(0)}` : a.threshold.toFixed(2);
  return `${v} / ${t}`;
}

/** Mini value-vs-threshold bar. Fill turns red at/over threshold. */
function RatioBar({ alert }: { alert: SleeveAlert }) {
  if (!(alert.threshold > 0)) return <span />;
  const frac = Math.max(0, Math.min(1, alert.metric_value / alert.threshold));
  const color = frac >= 1 ? "var(--bad)" : "var(--warn)";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
      <span
        style={{
          width: 36, height: 4, borderRadius: 2, overflow: "hidden",
          background: "rgba(128,128,128,0.18)",
        }}
      >
        <span style={{ display: "block", width: `${frac * 100}%`, height: "100%", background: color }} />
      </span>
      <span className="mono small muted">{fmtMetric(alert)}</span>
    </span>
  );
}

function kindShort(kind?: string): string {
  switch (kind) {
    case "sim": return "sim";
    case "rithmic_direct": return "rithmic";
    case "tradovate": return "tradovate";
    case "sierra_bridged": return "sierra";
    case "topstepx": return "topstep";
    default: return kind ?? "";
  }
}

function AcctBadge({ id, accounts }: { id?: number | null; accounts: Account[] }) {
  if (id == null) return null;
  const a = accounts.find((x) => x.id === id);
  return (
    <span
      className={"pill" + (a?.kind === "sim" ? " kind" : a?.kind === "rithmic_direct" ? " good" : " dim")}
      style={{ fontSize: 10, flexShrink: 0 }}
      title={a?.name ?? `account ${id}`}
    >
      #{id} {kindShort(a?.kind)}
    </span>
  );
}

function AlertRow({ a, accounts }: { a: SleeveAlert; accounts: Account[] }) {
  const sev = a.severity === "critical" ? "bad" : a.severity === "warning" ? "warn" : "dim";
  return (
    <div
      className={"pcc-alert-row " + sev}
      title={`${a.alert_type} · ${a.message}${a.timestamp ? ` · ${new Date(a.timestamp).toLocaleString()}` : ""}`}
    >
      <span
        style={{
          display: "inline-block", width: 2, height: 22, marginRight: 4, flexShrink: 0,
          background: a.severity === "critical" ? "var(--bad)" : a.severity === "warning" ? "var(--warn)" : "var(--muted, #888)",
        }}
      />
      <div className="pcc-alert-body" style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span className="mono small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {a.preset}
          </span>
          <AcctBadge id={a.account_id} accounts={accounts} />
          <div className="spacer" />
          <span className="muted small" style={{ flexShrink: 0 }}>
            {relTime(a.timestamp)}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span className="small muted" style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {a.message}
          </span>
          <RatioBar alert={a} />
        </div>
      </div>
    </div>
  );
}

type HistEntry = SleeveAlert & { count: number };

export function AlertHistory({ activeAlerts, history, onClearHistory, accounts, scopeAccountId }: Props) {
  const [filter, setFilter] = useState<SeverityFilter>("all");
  const [showHistory, setShowHistory] = useState(false);

  // Active: dedupe, scope-filter, critical first.
  const scopedActive = useMemo(() => {
    const seen = new Set<string>();
    const arr: SleeveAlert[] = [];
    for (const a of activeAlerts) {
      if (!inScope(a, scopeAccountId)) continue;
      const k = alertKey(a);
      if (seen.has(k)) continue;
      seen.add(k);
      arr.push(a);
    }
    return arr.sort((x, y) =>
      x.severity === y.severity ? 0 : x.severity === "critical" ? -1 : y.severity === "critical" ? 1 : 0
    );
  }, [activeAlerts, scopeAccountId]);

  // Out-of-scope alerts (only meaningful when peeking one account).
  const otherAccounts = useMemo(() => {
    const seen = new Set<string>();
    let critical = 0;
    for (const a of activeAlerts) {
      if (a.account_id == null || a.account_id === scopeAccountId) continue;
      const k = alertKey(a);
      if (seen.has(k)) continue;
      seen.add(k);
      if (a.severity === "critical") critical += 1;
    }
    return { count: seen.size, critical };
  }, [activeAlerts, scopeAccountId]);

  // History: dedupe by condition key, keep most recent occurrence, count repeats.
  const filteredHistory = useMemo(() => {
    const byKey = new Map<string, HistEntry>();
    for (const a of history) {
      const k = alertKey(a);
      const existing = byKey.get(k);
      if (existing) {
        existing.count += 1;
      } else {
        byKey.set(k, { ...a, count: 1 });
      }
    }
    let arr = [...byKey.values()];
    if (filter !== "all") arr = arr.filter((a) => a.severity === filter);
    return arr; // history is prepended newest-first, so first-seen order = newest
  }, [history, filter]);

  return (
    <div className="card">
      <div
        className="row wrap"
        style={{ cursor: "pointer", marginBottom: 8, alignItems: "center", gap: 8 }}
        onClick={() => setShowHistory((v) => !v)}
      >
        <h2>Alerts</h2>
        <div className="spacer" />
        {history.length > 0 && (
          <span className="muted small">
            {history.length} historical · {showHistory ? "▲" : "▼"}
          </span>
        )}
      </div>

      {/* Active alerts — severity-colored full-width rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 8 }}>
        {scopedActive.map((a, i) => (
          <AlertRow key={`act-${alertKey(a)}-${i}`} a={a} accounts={accounts} />
        ))}
      </div>

      {otherAccounts.count > 0 && scopeAccountId != null && (
        <div
          className="pcc-alert-row warn"
          style={{ marginBottom: 8, justifyContent: "center" }}
          title="Alerts on other accounts — switch scope or view All to inspect"
        >
          <span className="small muted">
            {otherAccounts.count} alert{otherAccounts.count > 1 ? "s" : ""} on other accounts
            {otherAccounts.critical > 0 ? ` · ${otherAccounts.critical} critical` : ""}
          </span>
        </div>
      )}

      {scopedActive.length === 0 && !showHistory && (
        <div className="muted small">no active alerts</div>
      )}

      {showHistory && (
        <>
          <div className="row" style={{ gap: 4, marginBottom: 8 }}>
            {(["all", "critical", "warning", "info"] as SeverityFilter[]).map((s) => (
              <button
                key={s}
                className={"btn btn-xs" + (filter === s ? " btn-active" : "")}
                onClick={() => setFilter(s)}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
            <div className="spacer" />
            <button className="btn btn-xs" onClick={onClearHistory}>
              Clear
            </button>
          </div>

          {filteredHistory.length === 0 ? (
            <div className="muted small">no matching alerts</div>
          ) : (
            <div style={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
              {filteredHistory.map((a, i) => (
                <div key={`h-${alertKey(a)}-${i}`} style={{ position: "relative" }}>
                  {a.count > 1 && (
                    <span
                      className="pill dim"
                      style={{ position: "absolute", top: -6, right: 0, fontSize: 9, zIndex: 1 }}
                      title="Occurrences this session"
                    >
                      ×{a.count}
                    </span>
                  )}
                  <AlertRow a={a} accounts={accounts} />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
